import posthog from 'posthog-js'
import { environnementPostHog, adresseRelais, UI_HOST_POSTHOG } from '../utils/posthog-environnement.js'
import { OPTIONS_MASQUAGE_REPLAY, OPTIONS_MASQUAGE_POSTHOG } from '../utils/posthog-masquage.js'

/**
 * Analyse du comportement. Sans donnée saisie : les heatmaps ne gardent que des
 * sélecteurs et des coordonnées, les clics morts suivent `mask_all_text` et
 * `mask_all_element_attributes` (posés par OPTIONS_MASQUAGE_POSTHOG).
 * `capture_heatmaps` est le nom courant de `enable_heatmaps`, que posthog-js
 * 1.335 ne lit plus que lorsque le premier est absent.
 */
export const OPTIONS_ANALYSE_POSTHOG = Object.freeze({
  capture_heatmaps: true,
  capture_dead_clicks: true,
  capture_performance: Object.freeze({ web_vitals: true })
})

/**
 * Seules les sessions des commerciaux sont enregistrées (replay) : ni l'équipe
 * d'administration, ni les comptes externes, ni les visiteurs des pages
 * publiques. Les événements et les erreurs partent pour tous.
 */
export const ROLES_ENREGISTRES = Object.freeze(['commercial'])

export function sessionEnregistree(user) {
  return Boolean(user) && ROLES_ENREGISTRES.includes(user.role)
}

let posthogInitialized = false
// Reposé après chaque `posthog.reset()`, qui vide les super-propriétés.
let environnementCourant = null

/**
 * Envoie un événement métier. Ne fait rien tant que PostHog n'est pas
 * initialisé (dev, localhost, tests) et ne lève jamais : un suivi en panne ne
 * doit pas casser le parcours du visiteur. L'appelant ne passe aucune donnée
 * saisie dans `proprietes`.
 */
export function suivreEvenement(nom, proprietes = {}) {
  if (!posthogInitialized || !nom) return
  try {
    posthog.capture(nom, proprietes)
  } catch {
    // Silencieux : le suivi n'est jamais bloquant.
  }
}

/**
 * Composable pour PostHog
 * Initialise PostHog pour le session replay et le tracking d'erreurs
 */
export function usePostHog() {
  // Ne pas enregistrer les sessions sur le formulaire embed
  const isEmbedForm = typeof window !== 'undefined' && window.location.pathname.startsWith('/embed')

  // Initialiser PostHog une seule fois, sur la production et le staging seulement
  // (cf. utils/posthog-environnement.js : un build servi en QA n'envoie rien).
  const environnement = typeof window !== 'undefined' ? environnementPostHog(window.location.hostname) : null

  if (!posthogInitialized && environnement) {
    posthog.init('phc_kKRn8x8BsyGcxqNKNAuhHubxkQq28OHYE9UwSU203Cd', {
      // Par le relais du serveur : même origine, ni CSP ni bloqueur ne l'arrêtent.
      api_host: adresseRelais(window.location.origin),
      ui_host: UI_HOST_POSTHOG,
      // Un profil seulement pour qui s'identifie (équipe, formateurs) : les visiteurs
      // anonymes du formulaire embarqué n'en créent pas.
      person_profiles: 'identified_only',
      ...OPTIONS_ANALYSE_POSTHOG,
      // Adresses, attributs des snapshots et autocapture sans donnée personnelle
      // (cf. utils/posthog-masquage.js).
      ...OPTIONS_MASQUAGE_POSTHOG,
      // Coupé au démarrage : identifyUser l'allume pour un commercial
      // (cf. ROLES_ENREGISTRES), jamais sur le formulaire embed.
      disable_session_recording: true,
      session_recording: {
        // Capturer les polices pour afficher correctement les icônes FontAwesome
        // Cela permet à PostHog de capturer les polices personnalisées pendant l'enregistrement
        collectFonts: true,
        // Intégrer les stylesheets pour une meilleure capture des styles et des icônes
        inlineStylesheet: true,
        // Capturer le contenu des éléments canvas pour les sessions replay
        recordCanvas: true,
        // Saisies masquées sauf dans les filtres des listes, données sensibles
        // masquées dans le texte, réseau sans corps ni en-têtes
        // (cf. utils/posthog-masquage.js).
        ...OPTIONS_MASQUAGE_REPLAY
      },
      loaded: (posthog) => {
        if (process.env.NODE_ENV === 'development') {
          console.log('PostHog initialized with session replay and error tracking (fonts and canvas collection enabled)')
        }
      }
    })
    // Sur chaque événement : les replays se filtrent par environnement.
    posthog.register({ environnement })
    environnementCourant = environnement
    posthogInitialized = true
  }

  /**
   * Identifier un utilisateur dans PostHog
   * Permet d'associer les sessions replay aux utilisateurs
   */
  const identifyUser = (user) => {
    if (!user || !posthogInitialized) return
    
    posthog.identify(user.id.toString(), {
      email: user.email,
      role: user.role,
      nom: user.nom,
      prenom: user.prenom
    })

    if (sessionEnregistree(user) && !isEmbedForm) {
      posthog.startSessionRecording()
    } else {
      posthog.stopSessionRecording()
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: User identified', user.email)
    }
  }

  /**
   * Réinitialiser l'identification de l'utilisateur
   */
  const resetUser = () => {
    if (!posthogInitialized) return
    posthog.stopSessionRecording()
    posthog.reset()
    // Sans quoi la session suivante (reconnexion sans rechargement) part sans
    // environnement et sort des filtres de replay.
    posthog.register({ environnement: environnementCourant })
    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: User reset')
    }
  }

  /**
   * Capturer une erreur manuellement (pour enrichir les erreurs avec du contexte)
   */
  const captureException = (error, context = {}) => {
    if (!posthogInitialized) return
    
    // Ensure we capture the full stack trace for source map resolution
    const stackTrace = error.stack || (error instanceof Error ? error.toString() : String(error))
    
    posthog.capture('$exception', {
      $exception_message: error.message || String(error),
      $exception_type: error.name || 'Error',
      $exception_stack_trace_raw: stackTrace,
      $exception_source: error.source || null,
      $exception_lineno: error.lineno || null,
      $exception_colno: error.colno || null,
      ...context
    })
    if (process.env.NODE_ENV === 'development') {
      console.log('PostHog: Exception captured', error, context)
    }
  }

  return {
    posthog: posthogInitialized ? posthog : null,
    identifyUser,
    resetUser,
    captureException,
    suivreEvenement
  }
}
